import React from "react";
import { motion } from "framer-motion";
import { useLanguage } from "../../contexts/LanguageContext";

const ABOUT_IMG =
  "https://static.prod-images.emergentagent.com/jobs/d69863e8-72c5-45eb-a555-03296866412e/images/4ce9bd6272fe98022ae35d075c703dd30ea4bbe67e75ad95b271a4ded1bba375.png";

export const About = () => {
  const { t } = useLanguage();

  const stats = [
    { value: t.about.stat_1_value, label: t.about.stat_1_label },
    { value: t.about.stat_2_value, label: t.about.stat_2_label },
    { value: t.about.stat_3_value, label: t.about.stat_3_label },
  ];

  return (
    <section
      id="about"
      data-testid="about-section"
      className="relative bg-white py-24 sm:py-32 overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-start">
          {/* Left: Image with offset navy block */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.9 }}
            className="lg:col-span-5 relative"
          >
            <div className="absolute -top-6 -left-6 w-2/3 h-2/3 bg-[#0D2F4C] hidden md:block" />
            <div className="relative">
              <img
                src={ABOUT_IMG}
                alt="Boardroom"
                className="w-full h-[480px] object-cover relative z-10"
              />
            </div>
          </motion.div>

          {/* Right: Text */}
          <div className="lg:col-span-7 lg:pl-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="flex items-center gap-4 mb-8"
            >
              <span className="h-px w-10 bg-[#0D2F4C]" />
              <span
                data-testid="about-overline"
                className="text-xs uppercase tracking-[0.3em] font-bold text-[#0D2F4C]"
              >
                {t.about.overline}
              </span>
            </motion.div>

            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.1 }}
              data-testid="about-title"
              className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.1] text-[#0D2F4C] tracking-tight mb-10"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              {t.about.title}
            </motion.h2>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="space-y-6 text-[#4B5563] text-base leading-relaxed max-w-2xl"
            >
              <p data-testid="about-p1">{t.about.paragraph_1}</p>
              <p data-testid="about-p2">{t.about.paragraph_2}</p>
            </motion.div>

            {/* Stats */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.35 }}
              className="grid grid-cols-3 gap-6 mt-14 pt-10 border-t border-[#0D2F4C]/10"
            >
              {stats.map((s, i) => (
                <div key={i} data-testid={`about-stat-${i}`}>
                  <div
                    className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#0D2F4C] mb-2"
                    style={{ fontFamily: "'Playfair Display', serif" }}
                  >
                    {s.value}
                  </div>
                  <div className="text-xs uppercase tracking-[0.15em] text-[#4B5563]">
                    {s.label}
                  </div>
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};
